import { useRef, useState } from "react";
import { api } from "../../services/apiService";
import "./more-like-this-panel.scss";

/**
 * Flag-gated "More Like This" affordance shown on a reviewed question. Off by
 * default: renders null unless REACT_APP_MORE_LIKE_THIS_ENABLED === "true" (and
 * there is a seed + we are in review), so the surface is byte-identical to today.
 *
 * Layered UNDER that master flag is REACT_APP_MORE_LIKE_THIS_AUDIENCE
 * (off|tutors|all, case-insensitive; ABSENT = "all" so existing behavior is
 * byte-identical). With "tutors", the panel mounts only when the signed-in user's
 * role (the same localStorage "role" read the mentorships pages use) is
 * ROLE_TUTOR or ROLE_ADMIN; any unrecognized value fails closed to "off". This is
 * UX-only — the server enforces its own audience gate
 * (qlearn.vector-quiz.more-like-this.audience) and stays authoritative.
 *
 * On demand it calls the dark backend endpoint (QLearn-service PR #428) for the
 * top-N nearest active questions to the seed, hydrates their stems, and lists them
 * as targeted practice. Fails soft: an empty result -> "no strong matches, try
 * category practice"; any error -> a retry, never a crash of the exam screen.
 */
const stemExcerpt = (s, n = 160) => {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

// Provenance claim persisted on the quiz doc (free string, like POWER_SEARCH)
// so MLT practice quizzes are distinguishable in history/analytics.
export const MLT_QUIZ_SOURCE = "MORE_LIKE_THIS";

export const mltPracticeQuizName = (items) => {
  if (items.length === 1) {
    const stem = stemExcerpt(items[0].stem, 60);
    return stem ? `Practice: ${stem}` : `Practice question ${items[0].questionId}`;
  }
  return `More Like This practice (${items.length} questions)`;
};

/*
 * Params for practicing MLT results through the SAME server contract the
 * power-search build uses (powerSearchQuestionIds on POST /api/v2/quiz —
 * QLS #485: ids server-revalidated against the requesting user's own
 * entitlement, stale ids clamp the count, zero survivors 400s). Mirrors
 * buildPowerSearchQuizParams' deliberate divergences from the form defaults:
 * all-false questionOptions (an explicit pick must not be silently excluded
 * as "already used"), threshold off (a q-score floor would re-filter the
 * picks), noOfQuestions = ids.length. No categoryMap: MLT results are
 * cross-topic by design; the server treats absent as "no filter" and the
 * entitlement revalidation still binds. Pure so the contract is unit-testable.
 */
export const buildMltPracticeQuizParams = ({ userId, questionIds, name }) => ({
  name,
  userId,
  source: MLT_QUIZ_SOURCE,
  quizOptions: { tutorOn: false, timerOn: false, thresholdEnabled: false },
  noOfQuestions: questionIds.length,
  questionOptions: { all: false, unused: false, correct: false, incorrect: false, marked: false },
  powerSearchQuestionIds: [...questionIds],
});

/**
 * off|tutors|all audience gate, evaluated per-render like the master flag.
 * Absent env -> "all" (byte-identical default); "tutors" -> tutor/admin role
 * only (same role read as src/pages/mentorships/); "off" or ANY unrecognized
 * value (including a blank-bound "") -> off, never silently open.
 */
const audienceAllows = () => {
  const raw = process.env.REACT_APP_MORE_LIKE_THIS_AUDIENCE;
  if (raw === undefined || raw === null) {
    return true; // absent -> "all"
  }
  const value = String(raw).trim().toLowerCase();
  if (value === "all") {
    return true;
  }
  if (value === "tutors") {
    const role = typeof window !== "undefined" ? window.localStorage.getItem("role") : null;
    return role === "ROLE_TUTOR" || role === "ROLE_ADMIN";
  }
  return false; // "off" and anything unrecognized fail closed
};

const feedbackAllows = () => {
  if (process.env.REACT_APP_MLT_FEEDBACK_ENABLED !== "true") return false;
  const role = typeof window !== "undefined" ? window.localStorage.getItem("role") : null;
  return role === "ROLE_TUTOR" || role === "ROLE_ADMIN";
};

const REASONS = [
  ["OFF_TOPIC", "Off topic"],
  ["TOO_SIMILAR", "Too similar"],
  ["DIFFERENT_CONCEPT", "Different concept"],
  ["QUALITY_ISSUE", "Quality issue"],
  ["OTHER", "Other"],
];

export default function MoreLikeThisPanel({ seedQuestionId, show = true }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null); // null = not run, [] = ran/empty
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState({});
  const [practicing, setPracticing] = useState(null); // null | ids of the quiz being created
  const [practiceError, setPracticeError] = useState("");

  // The exam container stays mounted while the user navigates questions, so the
  // panel is never remounted and state fetched for one seed would otherwise
  // persist onto the next question. Reset in-render on seed change (never paints
  // a stale frame), and keep the current seed in a ref so a fetch still in
  // flight for a previous seed can never write its results back.
  const [stateSeed, setStateSeed] = useState(seedQuestionId);
  const seedRef = useRef(seedQuestionId);
  if (stateSeed !== seedQuestionId) {
    setStateSeed(seedQuestionId);
    setLoading(false);
    setItems(null);
    setError("");
    setFeedback({});
    setPracticing(null);
    setPracticeError("");
  }
  seedRef.current = seedQuestionId;

  // Read the flags per-render so the surface flips exactly with the env flags
  // (master flag off by default; audience layered under it, absent = all).
  const flagOn = process.env.REACT_APP_MORE_LIKE_THIS_ENABLED === "true";
  if (!flagOn || !audienceAllows() || !show || !seedQuestionId) {
    return null;
  }

  const run = async () => {
    // Everything below an await races question navigation: bind this request to
    // the seed it was fired for and drop every write once the seed has moved on
    // (the in-render reset above already put the new question's panel in the
    // unrun state; a new request may even be in flight for it).
    const seed = seedQuestionId;
    const isCurrent = () => seedRef.current === seed;
    setLoading(true);
    setError("");
    try {
      const res = await api.getMoreLikeThis(seed);
      if (!isCurrent()) return;
      // doGetCall never rejects: a 200 resolves to the body; ANY non-200 (JSON or
      // empty body) to `{status, errMsg}`; only a network drop to an Error object. Route
      // hard failures (Error, or 5xx) to the Retry path; treat a 404 (seed absent / backend
      // flag off) and a 200 with no items as "no strong matches" (the category-practice fallback).
      const status = res && typeof res === "object" ? res.status : undefined;
      if (res instanceof Error || (typeof status === "number" && status >= 500)) {
        setError("Couldn't load related questions. Please try again.");
        setItems(null);
        return;
      }
      const raw = Array.isArray(res?.items) ? res.items.filter((r) => r && r.questionId) : [];
      if (!raw.length) {
        setItems([]);
        return;
      }
      // Hydrate stems for display (same helper the exam/power-search flows use).
      const ids = raw.map((r) => r.questionId);
      let stemById = {};
      try {
        const hydrated = await api.getQuestionsByIds(ids);
        const list = Array.isArray(hydrated) ? hydrated : (hydrated?.question_list || hydrated?.questions || []);
        list.forEach((q) => {
          const id = q?.questionId || q?.id;
          if (id) stemById[`${id}`] = q?.stem || "";
        });
      } catch (e) {
        stemById = {}; // hydration is best-effort; ids still render
      }
      if (!isCurrent()) return;
      setItems(raw.map((r) => ({
        questionId: r.questionId,
        cosine: r.cosine,
        crossTopic: !!r.crossTopic,
        stem: stemById[`${r.questionId}`] || "",
      })));
    } catch (e) {
      if (!isCurrent()) return;
      setError("Couldn't load related questions. Please try again.");
      setItems(null);
    } finally {
      // A stale request must not clear the spinner of a newer one.
      if (isCurrent()) setLoading(false);
    }
  };

  const feedbackOn = feedbackAllows();
  const submitFeedback = async (it, resultRank, patch = {}) => {
    const seed = seedQuestionId;
    const isCurrent = () => seedRef.current === seed;
    const previous = feedback[it.questionId] || {};
    const next = { ...previous, ...patch, saving: true, failed: false };
    setFeedback((all) => ({ ...all, [it.questionId]: next }));
    const body = {
      candidateQuestionId: it.questionId,
      verdict: next.verdict,
      cosine: it.cosine,
      crossTopic: it.crossTopic,
      resultRank,
    };
    if (next.reasonCode) body.reasonCode = next.reasonCode;
    if (next.comment?.trim()) body.comment = next.comment.trim();
    try {
      const res = await api.postMoreLikeThisFeedback(seed, body);
      if (!isCurrent()) return;
      const failed = res instanceof Error || (res && typeof res.errStatus === "number");
      setFeedback((all) => ({ ...all, [it.questionId]: { ...all[it.questionId], saving: false, failed } }));
    } catch (e) {
      if (!isCurrent()) return;
      setFeedback((all) => ({ ...all, [it.questionId]: { ...all[it.questionId], saving: false, failed: true } }));
    }
  };

  // Start a practice quiz from explicit MLT picks (per-row = one id, the
  // panel-level button = all of them). doUpdateCall never rejects: success is
  // a created-quiz body with an id, anything else is {errStatus, errMsg}.
  const practice = async (practiceItems) => {
    if (practicing) return;
    const seed = seedQuestionId;
    const isCurrent = () => seedRef.current === seed;
    const ids = practiceItems.map((it) => it.questionId);
    setPracticing(ids);
    setPracticeError("");
    try {
      const res = await api.createQuiz(buildMltPracticeQuizParams({
        userId: window.localStorage.getItem("userId"),
        questionIds: ids,
        name: mltPracticeQuizName(practiceItems),
      }));
      // Same race rule as run(): if the tutor moved to another question while
      // the create was in flight, don't yank the page out from under them —
      // a created quiz still shows up in quiz history.
      if (!isCurrent()) return;
      if (res?.id) {
        // Full-page navigation on purpose: an exam→exam SPA push lands in the
        // same mounted ExamPage, where the quiz fetch does re-key on examId
        // but onExamCompLoad, the unload/cleanup wiring, and per-quiz local
        // state are mount-bound — pushed params would mix the old quiz's
        // session state into the new one. `practicing` stays set so the
        // buttons remain disabled while the page unloads.
        window.location.assign(`/exam/${res.id}/1`);
        return;
      }
      setPracticeError("Couldn't start the practice quiz. Please try again.");
      setPracticing(null);
    } catch (e) {
      if (!isCurrent()) return;
      setPracticeError("Couldn't start the practice quiz. Please try again.");
      setPracticing(null);
    }
  };

  return (
    <div className={`more-like-this-panel${feedbackOn ? " mlt-feedback-enabled" : ""}`} data-testid="more-like-this-panel">
      {items === null && !error && (
        <button
          type="button"
          className="mlt-trigger"
          data-testid="more-like-this-trigger"
          onClick={run}
          disabled={loading}
        >
          {loading ? "Finding related questions…" : "More like this"}
        </button>
      )}

      {error && (
        <div className="mlt-error" data-testid="more-like-this-error">
          <span>{error}</span>
          <button type="button" className="mlt-retry" onClick={run} disabled={loading}>Retry</button>
        </div>
      )}

      {Array.isArray(items) && items.length === 0 && (
        <div className="mlt-empty" data-testid="more-like-this-empty">
          No closely-related questions found &mdash; try category practice.
        </div>
      )}

      {Array.isArray(items) && items.length > 0 && (
        <div className="mlt-results" data-testid="more-like-this-results">
          <div className="mlt-heading">Practice more like this</div>
          <ul>
            {items.map((it, index) => !feedbackOn ? (
              <li key={it.questionId} className="mlt-item" data-testid="more-like-this-item">
                <span className="mlt-stem">{stemExcerpt(it.stem) || `Question ${it.questionId}`}</span>
                <span className="mlt-meta">
                  {typeof it.cosine === "number" && (
                    <span className="mlt-sim" title="semantic similarity">{Math.round(it.cosine * 100)}%</span>
                  )}
                  {it.crossTopic && <span className="mlt-cross" title="from a related topic">related topic</span>}
                </span>
                <button
                  type="button"
                  className="mlt-practice"
                  data-testid="more-like-this-practice"
                  onClick={() => practice([it])}
                  disabled={!!practicing}
                >
                  {practicing && practicing.length === 1 && practicing[0] === it.questionId ? "Starting…" : "Practice"}
                </button>
              </li>
            ) : (
              <li key={it.questionId} className="mlt-item mlt-item-feedback-enabled" data-testid="more-like-this-item">
                <div className="mlt-item-summary">
                  <span className="mlt-stem">{stemExcerpt(it.stem) || `Question ${it.questionId}`}</span>
                  <span className="mlt-meta">
                  {typeof it.cosine === "number" && (
                    <span className="mlt-sim" title="semantic similarity">{Math.round(it.cosine * 100)}%</span>
                  )}
                  {it.crossTopic && <span className="mlt-cross" title="from a related topic">related topic</span>}
                  </span>
                  <button
                    type="button"
                    className="mlt-practice"
                    data-testid="more-like-this-practice"
                    onClick={() => practice([it])}
                    disabled={!!practicing}
                  >
                    {practicing && practicing.length === 1 && practicing[0] === it.questionId ? "Starting…" : "Practice"}
                  </button>
                </div>
                {(() => {
                  const row = feedback[it.questionId] || {};
                  return <div className="mlt-feedback" data-testid={`mlt-feedback-${it.questionId}`} aria-busy={!!row.saving}>
                    <div className="mlt-verdicts">
                      <button type="button" aria-label="Helpful" aria-pressed={row.verdict === "HELPFUL"}
                        className={row.verdict === "HELPFUL" ? "selected" : ""}
                        onClick={() => submitFeedback(it, index + 1, { verdict: "HELPFUL", reasonCode: undefined })}>👍</button>
                      <button type="button" aria-label="Not helpful" aria-pressed={row.verdict === "NOT_HELPFUL"}
                        className={row.verdict === "NOT_HELPFUL" ? "selected" : ""}
                        onClick={() => submitFeedback(it, index + 1, { verdict: "NOT_HELPFUL" })}>👎</button>
                    </div>
                    {row.verdict === "NOT_HELPFUL" && <div className="mlt-reasons" aria-label="Reason">
                      {REASONS.map(([value, label]) => <button type="button" key={value}
                        className={row.reasonCode === value ? "selected" : ""}
                        aria-pressed={row.reasonCode === value}
                        onClick={() => setFeedback((all) => ({ ...all, [it.questionId]: { ...all[it.questionId], reasonCode: value } }))}>{label}</button>)}
                    </div>}
                    {row.verdict && <div className="mlt-comment">
                      <label htmlFor={`mlt-comment-${it.questionId}`}>Optional comment</label>
                      <input id={`mlt-comment-${it.questionId}`} maxLength={280} value={row.comment || ""}
                        onInput={(e) => {
                          const comment = e.currentTarget.value;
                          setFeedback((all) => ({ ...all, [it.questionId]: { ...all[it.questionId], comment } }));
                        }} />
                      <small>Don't include student names or details.</small>
                      <button type="button" className="mlt-save" onClick={() => submitFeedback(it, index + 1)}>Save</button>
                    </div>}
                    {row.failed && <div className="mlt-feedback-error">Couldn't save feedback. <button type="button" onClick={() => submitFeedback(it, index + 1)}>Retry</button></div>}
                  </div>;
                })()}
              </li>
            ))}
          </ul>
          {items.length > 1 && (
            <button
              type="button"
              className="mlt-practice mlt-practice-all"
              data-testid="more-like-this-practice-all"
              onClick={() => practice(items)}
              disabled={!!practicing}
            >
              {practicing && practicing.length > 1 ? "Starting practice quiz…" : `Practice all ${items.length} related`}
            </button>
          )}
          {practiceError && (
            <div className="mlt-practice-error" data-testid="more-like-this-practice-error">{practiceError}</div>
          )}
        </div>
      )}
    </div>
  );
}
